// BHP-1116: destroy a VPS at Hetzner.
//
// `DELETE /api/vps/{uuid}` (BHP-1113) removes the machine at Hetzner first and
// retires our row (soft delete) after, so a 204 means the server is really gone.
// The caller navigates back to the overview on success, and two caches still
// disagree with that: the collection behind the overviews holds the deleted row,
// and the live-state query for this server is now pointing at nothing. Both are
// invalidated — the same half-truth the power actions and the edit form already
// guard against.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { httpClient } from '../../../shared/http/httpClient';
import { vpsDetailKeys } from './vpsDetailQueries';

export function useVpsDelete(uuid: string): {
  remove: () => Promise<void>;
  isPending: boolean;
  error: unknown;
} {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (): Promise<void> => {
      await httpClient.delete(`/vps/${uuid}`);
    },
    // Awaited, like the power actions: the mutation stays pending until the
    // overview list has actually been refetched. A caller that re-enabled its
    // controls early would be acting on a list that still showed the server it
    // just deleted.
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['hydra', 'vps', 'collection']
        }),
        queryClient.invalidateQueries({
          queryKey: vpsDetailKeys.state(uuid)
        })
      ]);
    }
  });

  return {
    remove: () => mutation.mutateAsync(),
    isPending: mutation.isPending,
    error: mutation.error
  };
}
